import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ModalWrapper from 'components/ModalWrapper';
import PropTypes from 'prop-types';

const PERMISSION_GROUPS = [
    { label: 'roles.permission_groups.dashboard',      keys: ['dashboard:view'] },
    { label: 'roles.permission_groups.analytics',      keys: ['analytics:view'] },
    { label: 'roles.permission_groups.users',          keys: ['users:view', 'users:export'] },
    { label: 'roles.permission_groups.campaigns',      keys: ['campaigns:view', 'campaigns:send'] },
    { label: 'roles.permission_groups.data_training',  keys: ['data-training:view', 'data-training:manage'] },
    { label: 'roles.permission_groups.content',        keys: ['faqs:manage', 'mainmenu:manage', 'announcements:manage'] },
    { label: 'roles.permission_groups.administration', keys: ['team:manage', 'roles:manage'] },
];

const MANDATORY_PERMISSIONS = ['dashboard:view'];

function RoleFormModal({ open, onClose, onSubmit, role }) {
    const { t } = useTranslation();
    const isEdit = !!role;
    const isBlocked = role?.name === 'Blocked';

    const { register, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm({
        defaultValues: { name: '', permissions: [] }
    });

    // Register the permissions field once for validation only — checkboxes are controlled below
    register('permissions', { validate: v => (Array.isArray(v) && (isBlocked || v.length > 0)) || t('roles.permissions_required') });

    useEffect(() => {
        if (open) {
            const base = role?.permissions ?? [];
            reset({
                name: role?.name ?? '',
                permissions: isBlocked
                    ? base
                    : Array.from(new Set([...MANDATORY_PERMISSIONS, ...base])),
            });
        }
    }, [open, role, reset, isBlocked]);

    const selectedPermissions = watch('permissions');

    const handlePermissionChange = (key, isMandatory) => (e) => {
        if (isMandatory) return;
        const next = e.target.checked
            ? [...selectedPermissions, key]
            : selectedPermissions.filter(p => p !== key);
        setValue('permissions', next, { shouldValidate: true });
    };

    const onFormSubmit = async (data) => {
        const permissions = isBlocked
            ? data.permissions
            : Array.from(new Set([...MANDATORY_PERMISSIONS, ...data.permissions]));
        await onSubmit({ ...data, permissions });
        onClose();
    };

    return (
        <ModalWrapper
            open={open}
            setOpen={onClose}
            title={isEdit ? t('roles.edit_role') : t('roles.create_role')}
        >
            <form onSubmit={handleSubmit(onFormSubmit)} className="w-full min-w-96">

                {/* Role name */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('roles.role_name')}
                    </label>
                    <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isEdit && role?.isProtected}
                        {...register('name', { required: t('roles.name_required') })}
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
                    {isEdit && role?.isProtected && (
                        <p className="text-gray-400 text-xs mt-1">{t('roles.protected_name_hint')}</p>
                    )}
                </div>

                {/* Permissions */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('roles.permissions')}
                    </label>
                    {errors.permissions && <p className="text-red-500 text-xs mb-2">{errors.permissions.message}</p>}

                    <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                        {PERMISSION_GROUPS.map((group) => (
                            <div key={group.label}>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{t(group.label)}</p>
                                <div className="space-y-2 pl-2">
                                    {group.keys.map((key) => {
                                        const isMandatory = !isBlocked && MANDATORY_PERMISSIONS.includes(key);
                                        const descKey = `roles.permission_descriptions.${key.replace(/[:.]/g, '_')}`;
                                        return (
                                            <label key={key} className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    value={key}
                                                    checked={isMandatory || selectedPermissions.includes(key)}
                                                    disabled={isMandatory}
                                                    className={`rounded border-gray-300 mt-0.5 flex-shrink-0 ${isMandatory ? 'opacity-60 cursor-not-allowed' : 'text-blue-600'}`}
                                                    onChange={handlePermissionChange(key, isMandatory)}
                                                />
                                                <div>
                                                    <span>
                                                        {t(`roles.permission_labels.${key.replace(/[:.]/g, '_')}`)}
                                                        {isMandatory && (
                                                            <span className="text-xs text-gray-400 ml-1">({t('roles.mandatory')})</span>
                                                        )}
                                                    </span>
                                                    <p className="text-xs text-gray-400 mt-0.5">{t(descKey)}</p>
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                        {t('actions.cancel')}
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                        {isEdit ? t('actions.update') : t('actions.create')}
                    </button>
                </div>
            </form>
        </ModalWrapper>
    );
}

RoleFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  role: PropTypes.object,
};

export default RoleFormModal;
